<template>
    <div class="vs-number-input-container relative">
        <vx-input
            ref="inputRef"
            class="vx-number-input"
            :value="localValue"
            v-bind="cmpAttrs"
            v-on="listeners"
            v-mask="appliedMask"
            inputmode="tel"
        />
    </div>
</template>

<script>
// Integer-only Boolean prop
// <m>ake sure the pattern, type and inputtype are set appropriately
// Catch the input events and decide whether any characters are or aren't
// allowed before raising the input event again(possibly use a regex replace on non - numbers)
// Min - max props for generated validation rules
// Decide on an appropriate icon (and provide a hide-icon prop)
const DEFAULT_BACKUP = 'a'

export default {
    name: 'vx-number-input',
    props: {
        value: {
            type: [String, Number]
        },
        integerOnly: {
            type: Boolean,
            default: false
        },
        min: {
            type: [Number, String]
        },
        max: {
            type: [Number, String]
        },
        mask: {
            type: [Function, Array, String],
            default: null
        },
        placeholder: {
            type: String,
            default: null
        },
        rules: {
            type: Array,
            default: () => []
        },
        decimalPlaces: {
            type: [Number, String],
            default: null
        }
    },
    data() {
        return {
            appliedMask: '',
            backupValue: DEFAULT_BACKUP
        }
    },
    watch: {
        maxIntegralLength: {
            handler: function () {
                this.appliedMask = this.getMask(this.value)
            },
            immediate: true
        },
        maxDecimalLength: {
            handler: function () {
                this.appliedMask = this.getMask(this.value)
            },
            immediate: true
        }
    },
    computed: {
        localValue: {
            get() {
                return this.value
            },
            set(val, oldVal) {
                this.appliedMask = this.getMask(val)
                this.$emit('input', val || null)
            }
        },
        cmpAttrs() {
            return {
                ...this.$attrs,
                placeholder: this.cmpPlaceholder,
                rules: this.cmpRules
            }
        },
        cmpPlaceholder() {
            if (this.placeholder) {
                return this.placeholder
            }
            if (this.mask) {
                return this.mask
            }
            var integralLength = this.maxIntegralLength || 2
            var decimalLength = this.maxDecimalLength == null ? 2 : this.maxDecimalLength
            var placeholder = '#'.repeat(integralLength)

            if (decimalLength) {
                placeholder += `.${'#'.repeat(decimalLength)}`
            }
            return placeholder
        },
        listeners() {
            return {
                ...this.$listeners,
                input: this.handleInput
            }
        },
        maxIntegralLength() {
            if (this.max == null || this.min == null) {
                return null
            }
            var min = String(this.min).replace('-', '')
            var max = String(this.max).replace('-', '')
            return Math.max(max.length, min.length)
        },
        maxDecimalLength() {
            if (this.integerOnly) {
                return 0
            }
            return this.decimalPlaces
        },
        cmpRules() {
            var rules = [...this.rules]
            if (this.min != null && this.max != null) {
                rules.push(`between:${this.min},${this.max}`)
            } else if (this.min != null) {
                rules.push(`min:${this.min}`)
            } else if (this.max != null) {
                rules.push(`max:${this.min}`)
            }
            if (this.decimalPlaces != null) {
                rules.push(`decimal:${this.decimalPlaces}`)
            }
            return rules
        },

        defaultMask() {
            var mask = []

            if (this.min != null && this.min < 0) {
                mask.push(/[\d-]/)
            }
            var integralLength = (this.maxIntegralLength || 2) - 1
            mask.push(/\d/)

            while (integralLength--) {
                mask.push(/\d/)
            }

            if (this.maxDecimalLength == 0) {
                return mask
            }
            mask.push('.', /\d/)

            var decimalLength = (this.maxDecimalLength || 2) - 1
            while (decimalLength--) {
                mask.push(/\d/)
            }
            return mask
        },
        canBeNegative() {
            return this.min == null || this.min < 0
        },
        canHaveDecimals() {
            return this.maxDecimalLength != 0
        }
    },
    methods: {
        handleInput(v) {
            this.localValue = v || null
        },
        getMask(value) {
            if (this.mask) {
                return this.mask
            }

            if (!value) {
                return this.defaultMask
            }

            var decimalFound = false
            var currentIntegralCount = 0
            var currentDecimalCount = 0

            return value.split('').reduce(
                function (current, char, index) {
                    if (!/[\d.-]/.test(char)) {
                        return current || '#'
                    }

                    if (char == '-') {
                        if (!this.canBeNegative || index != 0) {
                            return current || '#'
                        }
                        return current + '-'
                    }
                    if (char == '.') {
                        if (decimalFound || !this.canHaveDecimals) {
                            return current + '#'
                        }

                        decimalFound = true
                        if (index == 0) {
                            currentIntegralCount++
                            return current + '0.'
                        }
                        return current + '.'
                    }

                    if (!decimalFound || !this.canHaveDecimals) {
                        if (
                            this.maxIntegralLength == null ||
                            currentIntegralCount < this.maxIntegralLength
                        ) {
                            currentIntegralCount++
                            return current + '#'
                        }
                        if (this.canHaveDecimals) {
                            decimalFound = true
                            currentDecimalCount++
                            return `${current}.#`
                        }
                        return current || '#'
                    }

                    // The decimal has been found
                    if (
                        this.maxDecimalLength == null ||
                        currentDecimalCount < this.maxDecimalLength
                    ) {
                        currentDecimalCount++
                        return current + '#'
                    }
                    return current || '#'
                }.bind(this),
                ''
            )
        }
    }
}
</script>

<style lang="scss" scoped></style>
